import moment from 'moment';

import {
  AuthTokens,
  TIME_CONSTANTS,
  ERROR_MESSAGES,
  SyncStatus,
  EventStatus,
  STORAGE_KEYS,
  Event,
  CalendarProvider,
} from './authHelpers';
import { isGoogleProvider, isMicrosoftProvider, MICROSOFT_ROOM_ID_PROPERTY_FIELD } from './calendarHelpers';

// Raw API Response Types
interface GoogleRawEvent {
  id: string;
  summary?: string;
  description?: string;
  start?: {
    dateTime?: string;
    date?: string;
  };
  end?: {
    dateTime?: string;
    date?: string;
  };
  location?: string;
  status?: string;
  extendedProperties?: {
    private?: {
      roomId?: string;
      [key: string]: any;
    };
    shared?: {
      [key: string]: any;
    };
  };
  created?: string;
  updated?: string;
  calendarId?: string;
}

interface MicrosoftRawEvent {
  id: string;
  subject?: string;
  bodyPreview?: string;
  body?: {
    content?: string;
    contentType?: string;
  };
  start?: {
    dateTime?: string;
    timeZone?: string;
  };
  end?: {
    dateTime?: string;
    timeZone?: string;
  };
  location?:
    | {
        displayName?: string;
      }
    | string;
  showAs?: string;
  singleValueExtendedProperties?: Array<{
    id: string;
    value: string;
  }>;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
  calendarId?: string;
}

export type RawEvent = GoogleRawEvent | MicrosoftRawEvent;

// Date and Time Utilities
export const dateUtils = {
  /**
   * Format date for display
   */
  formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString(undefined, options);
  },

  /**
   * Format datetime for display
   */
  formatDateTime(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString();
  },

  /**
   * Get ISO string with timezone
   */
  toISOString(date: Date): string {
    return date.toISOString();
  },

  /**
   * Check if date is in the past
   */
  isPast(date: string | Date): boolean {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj < new Date();
  },

  /**
   * Get default time range (3 months)
   */
  getDefaultTimeRange(): { start: string; end: string } {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const end = new Date(now.getFullYear(), now.getMonth() + 2, now.getDate());

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    };
  },

  /**
   * Check if token is expired
   */
  isTokenExpired(tokens: AuthTokens): boolean {
    if (!tokens.obtainedAt || !tokens.expires_in) return true;
    const expiresAt = tokens.obtainedAt + tokens.expires_in * 1000 - TIME_CONSTANTS.TOKEN_EXPIRY_BUFFER;
    return Date.now() > expiresAt;
  },
};

// Error Handling Utilities
export const errorUtils = {
  /**
   * Create standardized error message
   */
  createErrorMessage(error: unknown, context: string): string {
    if (error instanceof Error) {
      return `${context}: ${error.message}`;
    }
    if (typeof error === 'string') {
      return `${context}: ${error}`;
    }
    return `${context}: ${ERROR_MESSAGES.GENERAL.UNKNOWN_ERROR}`;
  },

  /**
   * Handle API errors
   */
  handleApiError(response: Response, context: string): string {
    if (!response.ok) {
      return `${context}: ${response.status} ${response.statusText}`;
    }
    return '';
  },

  /**
   * Retry function with exponential backoff
   */
  async retry<T>(fn: () => Promise<T>, maxAttempts: number = 3, delay: number = 1000): Promise<T> {
    let lastError: Error;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (attempt === maxAttempts) {
          throw lastError;
        }

        // Exponential backoff
        await new Promise((resolve) => setTimeout(resolve, delay * Math.pow(2, attempt - 1)));
      }
    }

    throw lastError!;
  },
};

// Data Transformation Utilities
export const transformUtils = {
  /**
   * Normalize event data from different providers
   */
  normalizeEvent(rawEvent: RawEvent, provider: CalendarProvider): Event {
    if (isGoogleProvider(provider)) {
      const googleEvent = rawEvent as GoogleRawEvent;
      const startDate = googleEvent.start?.date ? moment(googleEvent.start.date).format('YYYY-MM-DDTHH:mm:ssZ') : null;
      const endDate = googleEvent.end?.date ? moment(googleEvent.end.date).format('YYYY-MM-DDTHH:mm:ssZ') : null;

      return {
        id: googleEvent.id || `temp_${Date.now()}_${Math.random()}`,
        title: googleEvent.summary || 'Untitled Event',
        description: googleEvent.description || '',
        startDate: googleEvent.start?.dateTime || startDate || '',
        endDate: googleEvent.end?.dateTime || endDate || '',
        location: googleEvent.location || '',
        status: (googleEvent.status as EventStatus) || 'confirmed',
        provider,
        calendarId: googleEvent.calendarId || '',
        remoteId: googleEvent.id,
        syncStatus: 'synced' as SyncStatus,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        roomId: googleEvent.extendedProperties?.private?.roomId,
      };
    } else {
      const microsoftEvent = rawEvent as MicrosoftRawEvent;
      return {
        id: microsoftEvent.id || `temp_${Date.now()}_${Math.random()}`,
        title: microsoftEvent.subject || 'Untitled Event',
        description: microsoftEvent.bodyPreview || microsoftEvent.body?.content || '',
        startDate: microsoftEvent.start?.dateTime || '',
        endDate: microsoftEvent.end?.dateTime || '',
        location:
          typeof microsoftEvent.location === 'string'
            ? microsoftEvent.location
            : microsoftEvent.location?.displayName || '',
        status: 'confirmed' as EventStatus,
        provider,
        calendarId: microsoftEvent.calendarId || '',
        remoteId: microsoftEvent.id,
        syncStatus: 'synced' as SyncStatus,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        roomId: microsoftEvent.singleValueExtendedProperties?.find(
          (prop) => prop.id === MICROSOFT_ROOM_ID_PROPERTY_FIELD
        )?.value,
      };
    }
  },

  /**
   * Normalize calendar data from different providers
   */
  normalizeCalendar(rawCalendar: Record<string, unknown>, provider: CalendarProvider) {
    return {
      id: rawCalendar.id as string,
      name: (rawCalendar.summary as string) || (rawCalendar.name as string) || 'Untitled Calendar',
      provider,
      isPrimary: (rawCalendar.primary as boolean) || (rawCalendar.isDefaultCalendar as boolean) || false,
      isSelected: false,
      color: (rawCalendar.backgroundColor as string) || (rawCalendar.color as string) || '#4285f4',
      accessRole: (rawCalendar.accessRole as string) || 'reader',
      summary: (rawCalendar.summary as string) || (rawCalendar.name as string) || 'No Summary',
    };
  },

  /**
   * Prepare event for API submission
   */
  prepareEventForApi(event: Event, provider: CalendarProvider): Record<string, unknown> {
    // Convert datetime-local format to proper ISO 8601 format with timezone
    const formatDateTimeForAPI = (dateTimeString: string): string => {
      // datetime-local format is "YYYY-MM-DDTHH:MM" (no timezone)
      // We need to convert it to ISO 8601 with timezone
      const date = new Date(dateTimeString);
      return date.toISOString();
    };

    const baseEvent = {
      start: {
        dateTime: formatDateTimeForAPI(event.startDate ?? ''),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      end: {
        dateTime: formatDateTimeForAPI(event.endDate ?? ''),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    };

    if (isGoogleProvider(provider)) {
      const customPayload = isGoogleProvider(provider)
        ? { extendedProperties: { private: { roomId: event.roomId } } }
        : {};
      return {
        summary: event.title,
        description: event.description,
        location: event.location,
        extendedProperties: { private: { roomId: event.roomId } },
        ...baseEvent,
        ...customPayload,
      };
    } else if (isMicrosoftProvider(provider)) {
      return {
        subject: event.title,
        body: {
          contentType: 'text',
          content: event.description || '',
        },
        ...(event.location && {
          location: {
            displayName: event.location || '',
          },
        }),
        ...baseEvent,
      };
    }

    return baseEvent;
  },
};

// Validation Utilities
export const validationUtils = {
  /**
   * Validate event data
   */
  validateEvent(event: Partial<Event>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!event.title?.trim()) {
      errors.push('Event title is required');
    }

    if (!event.startDate) {
      errors.push('Start date is required');
    }

    if (!event.endDate) {
      errors.push('End date is required');
    }

    if (event.startDate && event.endDate) {
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);

      if (start >= end) {
        errors.push('End date must be after start date');
      }
    }

    if (event.title && event.title.length > 100) {
      errors.push('Event title must be 100 characters or less');
    }

    if (event.description && event.description.length > 500) {
      errors.push('Event description must be 500 characters or less');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Validate provider
   */
  isValidProvider(provider: string): provider is CalendarProvider {
    return ['google', 'microsoft', 'local'].includes(provider);
  },
};

// Storage Utilities
export const storageUtils = {
  CODE_VERIFIER: 'code_verifier',
  /**
   * Safe localStorage get
   */
  get<T>(key: string, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Error reading from localStorage (${key}):`, error);
      return defaultValue;
    }
  },

  removeTokens(): void {
    this.remove(STORAGE_KEYS.TOKENS);
  },

  loadTokens(): AuthTokens[] {
    return this.get<AuthTokens[]>(STORAGE_KEYS.TOKENS, []);
  },

  getProviderFromToken(token: AuthTokens): CalendarProvider {
    const { provider } = token;

    return provider === CalendarProvider.GOOGLE ? CalendarProvider.GOOGLE : CalendarProvider.MICROSOFT;
  },
  /**
   * Safe localStorage set
   */
  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error writing to localStorage (${key}):`, error);
    }
  },

  /**
   * Safe localStorage remove
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing from localStorage (${key}):`, error);
    }
  },
};

// Debounce Utility
export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: number;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Generate unique ID
export const generateId = (prefix: string = 'id'): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
